import { Suspense } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { KioskShell, PublicShell, StaffShell } from '@/components/shell';
import { authRoutes, kioskChildRoutes, publicRoutes, staffRoutes } from '@/features/routes';
import { ForgotPasswordPage, FullScreenLoader, RequireStaff, StaffLoginPage } from '@/lib/auth';
import { env } from '@/lib/env';
import { lazyNamed } from '@/lib/lazy';
import { NotFoundPage, RouteError } from './system-pages';

const DesignSystemPage = lazyNamed(() => import('@/features/design-system/DesignSystemPage'), 'DesignSystemPage');

/**
 *   /staff/...        staff console (permission-filtered), /staff/login for sign-in
 *   /staff/design-system   living component gallery (dev builds, or VITE_SHOW_DESIGN_SYSTEM=true)
 *   /kiosk            self-checkout
 *   /login /register /forgot-password /reset-password   patron auth
 *   /...              public OPAC and /account
 */
const routes: RouteObject[] = [
  { path: '/staff/login', element: <StaffLoginPage />, errorElement: <RouteError /> },
  { path: '/staff/forgot-password', element: <ForgotPasswordPage staff />, errorElement: <RouteError /> },
  ...(env.showDesignSystem
    ? [
        {
          path: '/staff/design-system',
          element: (
            <Suspense fallback={<FullScreenLoader />}>
              <DesignSystemPage />
            </Suspense>
          ),
          errorElement: <RouteError />,
        } satisfies RouteObject,
      ]
    : []),
  {
    path: '/staff',
    element: (
      <RequireStaff>
        <StaffShell />
      </RequireStaff>
    ),
    errorElement: <RouteError />,
    children: staffRoutes,
  },
  { path: '/kiosk', element: <KioskShell />, errorElement: <RouteError />, children: kioskChildRoutes },
  ...authRoutes.map((route) => ({ ...route, path: `/${route.path ?? ''}`, errorElement: <RouteError /> })),
  { path: '/', element: <PublicShell />, errorElement: <RouteError />, children: publicRoutes },
  { path: '*', element: <NotFoundPage /> },
];

export const router = createBrowserRouter(routes);
